import { ContentHeader } from "../components/ContentHeader";
import { useApi, type ApiState } from "../hooks/useApi";
import { SCAFFOLDS } from "./scaffolds";

// ---- Response shapes (local — not shared via api/types.ts, matching RuntimeTab) ----
type ConnStatusLabel = "ok" | "failing" | "alerting";

interface ConnStatus {
  connection_id: string;
  connection_name: string | null;
  status: ConnStatusLabel;
  consecutive_failures: number;
  alerting: boolean;
  streak_started_at: string | null;
  last_failed_at: string | null;
  last_succeeded_at: string | null;
  last_received_at: string | null;
  last_alert_at: string | null;
}
interface ConnectionsResponse {
  status: "ok";
  connections: ConnStatus[];
  summary: { alerting: number; failing: number; ok: number; total: number };
}

interface FailureEvent {
  id: string;
  connection_id: string;
  connection_name: string | null;
  job_id: number | null;
  error_type: string | null;
  error_message: string | null;
  error_origin: string | null;
  received_at: string | null;
}
interface FailuresResponse {
  status: "ok";
  items: FailureEvent[];
  total: number;
}

// This tab is a partially-live Wave-2 domain: keep the scaffold's structure (planned hero
// + planned-panels grid) and light up only the parts the Airbyte sync webhook now feeds.
// The remaining panels (freshness, schema-drift, row-counts) still await the Airbyte API +
// ClickHouse ingestion metadata (WEA-26), so they stay as scaffold previews.
const SPEC = SCAFFOLDS["data-pipelines"];
const LIVE_PANELS = new Set(["Per-connector status", "Job success / failure"]);

const STATUS_COLOR: Record<ConnStatusLabel, string> = {
  ok: "var(--green)",
  failing: "var(--yellow)",
  alerting: "var(--red)",
};
const STATUS_LABEL: Record<ConnStatusLabel, string> = {
  ok: "healthy",
  failing: "failing",
  alerting: "alerting",
};

function StatusBadge({ status }: { status: ConnStatusLabel }) {
  const color = STATUS_COLOR[status] ?? "var(--gray)";
  return (
    <span className="tier-badge" style={{ color, border: `1px solid ${color}55`, background: "transparent" }}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

// "3m ago" / "2h ago" / "5d ago"
function ago(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// duration from a start timestamp to now, e.g. "35m" / "2h 5m"
function since(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const m = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function DataPipelinesTab() {
  const connR = useApi<ConnectionsResponse>("/command-center/data-pipelines/connections");
  const failR = useApi<FailuresResponse>("/command-center/data-pipelines/failures?limit=15");

  const plannedPanels = SPEC.panels.filter((p) => !LIVE_PANELS.has(p));

  return (
    <>
      <ContentHeader title={SPEC.title} count={connR.data?.summary.total} description={SPEC.desc} />

      {/* ---------- LIVE: Airbyte sync status (fed by POST /webhooks/airbyte) ---------- */}
      <div className="section-header">
        <h2>Airbyte — sync status</h2><span className="count">live</span>
      </div>
      <AirbyteSection connR={connR} failR={failR} />

      {/* ---------- PLANNED: remaining pipeline panels (scaffold structure preserved) ---------- */}
      <div className="scaffold-hero">
        <span className="scaffold-badge">Planned · {SPEC.phase}</span>
        <p className="scaffold-dep">
          Lights up when connected: <strong>{SPEC.dependency}</strong>
        </p>
      </div>
      <div className="section-header">
        <h2>Planned panels</h2><span className="count">{plannedPanels.length}</span>
      </div>
      <div className="grid">
        {plannedPanels.map((p) => (
          <div key={p} className="card scaffold-panel" aria-disabled="true">
            <div className="card-head">
              <div className="card-name" style={{ fontFamily: "var(--font-sans)" }}>{p}</div>
              <span className="planned-tag">soon</span>
            </div>
            <div className="scaffold-skeleton" />
          </div>
        ))}
      </div>
    </>
  );
}

// The live Airbyte block. Kept as its own component so the tab body stays a simple compose
// of "live section + scaffold structure".
function AirbyteSection({
  connR,
  failR,
}: {
  connR: ApiState<ConnectionsResponse>;
  failR: ApiState<FailuresResponse>;
}) {
  if (connR.loading) {
    return <div className="loading"><div className="loading-spinner" /><p>Loading Airbyte sync status…</p></div>;
  }
  if (connR.error || !connR.data) {
    return <div className="empty">Could not load Airbyte status. Is command-center-api running?</div>;
  }

  const { connections, summary } = connR.data;
  const failures = failR.data?.items ?? [];

  return (
    <>
      <div className="cov-summary">
        <span className={`cov-pill ${summary.alerting ? "crit" : "ok"}`}>
          <span className="dot" /><strong>{summary.alerting}</strong> alerting
        </span>
        <span className={`cov-pill ${summary.failing ? "warn" : "ok"}`}>
          <span className="dot" /><strong>{summary.failing}</strong> failing
        </span>
        <span className="cov-pill ok">
          <span className="dot" /><strong>{summary.ok}</strong> healthy
        </span>
      </div>

      {connections.length === 0 ? (
        <div className="empty">No Airbyte connections seen yet — waiting for the first sync webhook.</div>
      ) : (
        <table className="cov-matrix">
          <thead>
            <tr>
              <th>Connection</th>
              <th>Status</th>
              <th>Consecutive failures</th>
              <th>Failing for</th>
              <th>Last failed</th>
              <th>Last succeeded</th>
            </tr>
          </thead>
          <tbody>
            {connections.map((c) => (
              <tr key={c.connection_id}>
                <td><code style={{ fontFamily: "var(--font-mono)" }}>{c.connection_name ?? c.connection_id}</code></td>
                <td><StatusBadge status={c.status} /></td>
                <td className="num" style={{ color: c.consecutive_failures > 0 ? "var(--red)" : "var(--text-muted)" }}>
                  {c.consecutive_failures}
                </td>
                <td className="num">{c.consecutive_failures > 0 ? since(c.streak_started_at) : "—"}</td>
                <td>{ago(c.last_failed_at)}</td>
                <td>{ago(c.last_succeeded_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 style={{ margin: "22px 0 10px", fontSize: 13, color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
        Recent failures
      </h3>
      {failures.length === 0 ? (
        <div className="empty">No failures recorded.</div>
      ) : (
        <table className="cov-matrix">
          <thead>
            <tr><th>Connection</th><th>Error type</th><th>Message</th><th>When</th></tr>
          </thead>
          <tbody>
            {failures.map((f) => (
              <tr key={f.id}>
                <td><code style={{ fontFamily: "var(--font-mono)" }}>{f.connection_name ?? f.connection_id}</code></td>
                <td>{f.error_type ?? "—"}</td>
                <td style={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={f.error_message ?? undefined}>
                  {f.error_message ?? "—"}
                </td>
                <td>{ago(f.received_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export default DataPipelinesTab;
